import React, { useState, useRef } from 'react';
import { Typography, Input, Button, Upload, Avatar, notification } from 'antd';
import { MailOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api';
import './SettingsPage.scss';
import { UploadPhotoIcon } from "../../constants/icons";
import { useUser } from '../../store/userStore';

const { Title, Text } = Typography;

const SettingsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, setUser } = useUser();

    const [username, setUsername] = useState(user.username);
    const [email, setEmail] = useState(user.email);
    const [role] = useState(user.role);
    const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const initialUsername = useRef(user.username);

    const handleSave = async () => {
        if (newPassword && newPassword !== confirmPassword) {
            notification.error({ message: t('settingsPage.passwordMismatch', 'Passwords do not match') });
            return;
        }

        const usernameChanged = username !== initialUsername.current;
        const passwordChanged = currentPassword && newPassword;

        if (!usernameChanged && !passwordChanged) {
            navigate('/profile');
            return;
        }

        setLoading(true);
        try {
            if (usernameChanged) {
                const res = await api.patch('/user/me', {
                    username,
                    first_name: "",
                    last_name: "",
                });
                if (res.data.access_token) {
                    localStorage.setItem('token', res.data.access_token);
                }
                if (res.data.refresh_token) {
                    localStorage.setItem('refreshToken', res.data.refresh_token);
                }
                if (res.data.username) {
                    localStorage.setItem('username', res.data.username);
                }
            }

            if (passwordChanged) {
                await api.patch('/user/me/password', {
                    current_password: currentPassword,
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                });
                notification.success({ message: t('settingsPage.passwordUpdated', 'Password updated successfully'), description: t('settingsPage.passwordUpdatedDescription', 'Use your new password next time you sign in.') });
            }

            setUser({ ...user, username, email });
            notification.success({ message: t('settingsPage.saved', 'Settings saved successfully'), description: t('settingsPage.savedDescription', 'Your profile settings have been saved.') });
            navigate('/profile');
        } catch (error) {
            notification.error({ message: t('settingsPage.saveError', 'Failed to save settings') });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const uploadProps = {
        beforeUpload: async (file) => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                notification.error({ message: t('settingsPage.imageOnly', 'You can only upload image files') });
                return false;
            }
            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                notification.error({ message: t('settingsPage.fileTooLarge', 'Image must be smaller than 2MB') });
                return false;
            }
            try {
                const formData = new FormData();
                formData.append('file', file);
                const res = await api.put('/user/me/image', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const newUrl = res.data.profile_image_url || URL.createObjectURL(file);
                setAvatarUrl(newUrl);
                setUser({ ...user, avatarUrl: newUrl });
                notification.success({ message: t('settingsPage.photoUpdated', 'Photo updated successfully'), description: t('settingsPage.photoUpdatedDescription', 'Your new photo is now visible on your profile.') });
            } catch (error) {
                notification.error({ message: t('settingsPage.photoError', 'Failed to upload photo') });
            }
            return false;
        },
        showUploadList: false,
    };

    return (
        <div className="settings-page">
            <div className="settings-page__header">
                <Title level={3} className="settings-page__title">
                    {t('settingsPage.title', 'Settings')}
                </Title>
                <div className="settings-page__actions">
                    <Button className="default-grey-button" onClick={handleCancel} style={{height: '40px'}}>
                        {t('settingsPage.cancel', 'Cancel')}
                    </Button>
                    <Button type="primary" className="default-button small" loading={loading} onClick={handleSave}>
                        {t('settingsPage.save', 'Save')}
                    </Button>
                </div>
            </div>

            <div className="settings-page__section">
                <Title level={5} className="settings-page__section-title">
                    {t('settingsPage.personalInfo', 'Personal info')}
                </Title>
                <Text className="settings-page__section-subtitle">
                    {t('settingsPage.personalInfoDesc', 'Update your photo and personal details here.')}
                </Text>
            </div>

            <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.username', 'Username')}
                </label>
                <div className="settings-page__field">
                    <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder={t('settingsPage.usernamePlaceholder', 'Username')}
                    />
                </div>
            </div>

            {/* <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.firstName', 'First name')}
                </label>
                <div className="settings-page__field">
                    <Input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder={t('settingsPage.firstNamePlaceholder', 'First name')}
                    />
                </div>
            </div> */}

            {/* <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.lastName', 'Last name')}
                </label>
                <div className="settings-page__field">
                    <Input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder={t('settingsPage.lastNamePlaceholder', 'Last name')}
                    />
                </div>
            </div> */}

            <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.emailAddress', 'Email address')}
                </label>
                <div className="settings-page__field">
                    <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        prefix={<MailOutlined style={{ color: '#A4A7AE' }} />}
                        placeholder={t('settingsPage.emailPlaceholder', 'email@example.com')}
                    />
                </div>
            </div>

            <div className="settings-page__row">
                <div className="settings-page__label-group">
                    <label className="settings-page__label">
                        {t('settingsPage.yourPhoto', 'Your photo')}
                    </label>
                    <Text className="settings-page__label-hint">
                        {t('settingsPage.photoHint', 'This will be displayed on your profile.')}
                    </Text>
                </div>
                <div className="settings-page__field settings-page__field--photo">
                    <Avatar size={64} src={avatarUrl} icon={!avatarUrl && <UserOutlined />} />
                    <Upload.Dragger {...uploadProps} className="settings-page__upload">
                        <p className="ant-upload-drag-icon">
                            <UploadPhotoIcon />
                        </p>
                        <p className="ant-upload-text">
                            <span className="settings-page__upload-link">
                                {t('settingsPage.clickToUpload', 'Click to upload')}
                            </span>
                            {' '}{t('settingsPage.orDragDrop', 'or drag and drop')}
                        </p>
                        <p className="ant-upload-hint">
                            {t('settingsPage.photoFormats', 'SVG, PNG, JPG or GIF (max. 800x400px)')}
                        </p>
                    </Upload.Dragger>
                </div>
            </div>

            <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.role', 'Role')}
                </label>
                <div className="settings-page__field">
                    <Input value={role} disabled />
                </div>
            </div>

            <div className="settings-page__section settings-page__section--password">
                <Title level={5} className="settings-page__section-title">
                    {t('settingsPage.password', 'Password')}
                </Title>
                <Text className="settings-page__section-subtitle">
                    {t('settingsPage.passwordDesc', 'Please enter your current password to change your password.')}
                </Text>
            </div>

            <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.currentPassword', 'Current password')}*
                </label>
                <div className="settings-page__field">
                    <Input.Password
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="••••••••"
                    />
                </div>
            </div>

            <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.newPassword', 'New password')}*
                </label>
                <div className="settings-page__field">
                    <Input.Password
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                    />
                </div>
            </div>

            <div className="settings-page__row">
                <label className="settings-page__label">
                    {t('settingsPage.confirmPassword', 'Confirm new password')}*
                </label>
                <div className="settings-page__field">
                    <Input.Password
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                    />
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
